// frontend/src/components/dashboard/pos/dialogs/SuccessDialog.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Typography,
  Chip,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  Zoom,
  Avatar
} from '@mui/material';
import { 
  CheckCircle, 
  Payment,
  DevicesOther,
  LocalAtm,
  CreditCard,
  QrCode
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Create a transition component for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const SuccessDialog = ({
  open,
  totalAmount,
  paymentMethod,
  deviceNumber
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const MotionDiv = motion.div;
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-close after 3 seconds
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        // Auto-close functionality can be added here if needed
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Get payment method icon and color
  const getPaymentMethodDetails = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return { icon: LocalAtm, color: '#10b981', label: t('pos.cash') };
      case 'card':
        return { icon: CreditCard, color: '#f59e0b', label: t('pos.card') };
      case 'qr':
        return { icon: QrCode, color: '#3b82f6', label: t('pos.qrPay') };
      default:
        return { icon: Payment, color: '#667eea', label: method || t('pos.cash') };
    }
  };

  const paymentDetails = getPaymentMethodDetails(paymentMethod);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted={false}
      hideBackdrop={false}
      disableRestoreFocus={true}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 3 : 4,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(16, 185, 129, 0.25)',
          maxWidth: isMobile ? '90vw' : '420px',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 70% 30%, rgba(16, 185, 129, 0.15) 0%, transparent 60%)',
            pointerEvents: 'none'
          }
        }
      }}
      aria-labelledby="success-dialog-title"
      aria-describedby="success-dialog-description"
    >
      <DialogContent sx={{ 
        textAlign: 'center', 
        p: isMobile ? 3 : 4,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Success Animation */}
        <MotionDiv
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1
          }}
        >
          <Box sx={{ 
            position: 'relative',
            display: 'inline-block',
            mb: isMobile ? 2 : 3
          }}>
            {/* Outer ring animation */}
            <MotionDiv
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.1, 0.3]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? 120 : 140,
                height: isMobile ? 120 : 140,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
                zIndex: 0
              }} />
            </MotionDiv>
            
            <CheckCircle sx={{ 
              fontSize: isMobile ? 80 : 100, 
              color: '#10b981',
              filter: 'drop-shadow(0 8px 16px rgba(16, 185, 129, 0.3))',
              position: 'relative',
              zIndex: 1
            }} />
          </Box>
        </MotionDiv>

        {/* Success Message */}
        <MotionDiv
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Typography 
            id="success-dialog-title"
            variant={isMobile ? "h5" : "h4"} 
            gutterBottom 
            sx={{ 
              color: '#059669', 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(5, 150, 105, 0.1)',
              mb: isMobile ? 1 : 2
            }}
          >
            {t('pos.dialog.paymentSuccessful')}
          </Typography>
        </MotionDiv>

        {/* Amount Display */}
        <MotionDiv
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Box sx={{ 
            mb: isMobile ? 2 : 3,
            p: isMobile ? 1.5 : 2,
            background: 'rgba(16, 185, 129, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#047857',
                mb: 0.5,
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}
            >
              {t('pos.dialog.totalAmount')}
            </Typography>
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              sx={{ 
                color: '#059669', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(5, 150, 105, 0.1)',
                fontSize: isMobile ? '1.8rem' : '2.2rem'
              }}
            >
              ${totalAmount.toFixed(2)}
            </Typography>
          </Box>
        </MotionDiv>

        {/* Payment Method */}
        <MotionDiv
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: deviceNumber ? (isMobile ? 2 : 3) : (isMobile ? 3 : 4),
            gap: 1
          }}>
            <Avatar sx={{ 
              bgcolor: paymentDetails.color,
              width: isMobile ? 32 : 36,
              height: isMobile ? 32 : 36
            }}>
              <paymentDetails.icon fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Chip 
              label={paymentDetails.label.toUpperCase()}
              sx={{ 
                background: `linear-gradient(45deg, ${paymentDetails.color}20, ${paymentDetails.color}10)`,
                color: paymentDetails.color,
                fontWeight: 700,
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                px: isMobile ? 1 : 2,
                border: `1px solid ${paymentDetails.color}30`
              }} 
            />
          </Box>
        </MotionDiv>

        {/* Device Number Display */}
        {deviceNumber && (
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <Divider sx={{ 
              my: isMobile ? 1.5 : 2, 
              bgcolor: 'rgba(16, 185, 129, 0.2)' 
            }} />
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: 1,
                mb: 1
              }}>
                <DevicesOther sx={{ 
                  color: '#059669',
                  fontSize: isMobile ? 18 : 20
                }} />
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.8rem' : '0.875rem'
                  }}
                >
                  {t('pos.deviceNumber')}:
                </Typography>
              </Box>
              <Chip 
                label={`#${deviceNumber}`}
                sx={{ 
                  background: 'rgba(16, 185, 129, 0.15)', 
                  color: '#059669',
                  fontWeight: 800,
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  py: isMobile ? 1 : 1.5,
                  px: isMobile ? 1.5 : 2,
                  border: '2px solid rgba(16, 185, 129, 0.3)'
                }} 
              />
            </Box>
          </MotionDiv>
        )}
        
        {/* Thank You Message */}
        <MotionDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
        >
          <Typography 
            id="success-dialog-description"
            variant={isMobile ? "body2" : "body1"} 
            sx={{ 
              color: '#047857',
              fontStyle: 'italic',
              fontSize: isMobile ? '0.9rem' : '1rem',
              mt: 1
            }}
          >
            {t('pos.dialog.thankYou') || 'Thank you for your business!'}
          </Typography>
        </MotionDiv>

        {/* Subtle Auto-close indicator */}
        <MotionDiv
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: 3, ease: "linear", delay: 0.5 }}
        >
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: 2,
            background: 'linear-gradient(90deg, #10b981, #059669)',
            borderRadius: '0 0 4px 4px'
          }} />
        </MotionDiv>
      </DialogContent>
    </Dialog>
  );
};

export default SuccessDialog;